import { useState } from 'react';
import {
  Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, Chip, IconButton, Tooltip,
  FormGroup, FormControlLabel, Checkbox, Typography, Divider, Snackbar,
} from '@mui/material';
import { Add, Settings, Block, CheckCircle } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { TableToolbar } from '../../components/TableToolbar';
import { StatusBadge } from '../../components/StatusBadge';
import { platformApi, type Tenant } from '../../api/platform.api';
import { extractApiError } from '../../api/error';

const MODULES = ['projects', 'procurement', 'boq', 'work_orders', 'finance', 'approvals', 'profitability', 'inventory', 'field', 'amc', 'mobile'];
const PLANS = ['starter', 'professional', 'enterprise', 'custom'];

export function TenantsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState<Tenant | null>(null);
  const [form, setForm] = useState({ name: '', subdomain: '', adminEmail: '', adminPassword: '', planCode: 'starter', primaryColor: '#1e40af' });
  const [modules, setModules] = useState<string[]>([]);
  const [planSel, setPlanSel] = useState('starter');
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['platform', 'tenants', page, limit, search], queryFn: () => platformApi.tenants({ page, limit, search: search || undefined }) });
  const usage = useQuery({ queryKey: ['platform', 'usage', manage?.id], queryFn: () => platformApi.usage(manage!.id), enabled: Boolean(manage) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['platform'] });

  const provision = useMutation({
    mutationFn: () => platformApi.provision(form),
    onSuccess: () => { setOpen(false); setToast('Tenant provisioned'); invalidate(); }, onError: (e) => setError(extractApiError(e)),
  });
  const setStatus = useMutation({ mutationFn: ({ id, status }: { id: number; status: string }) => platformApi.setStatus(id, status), onSuccess: () => { setToast('Status updated'); invalidate(); } });
  const saveModules = useMutation({ mutationFn: () => platformApi.setModules(manage!.id, modules), onSuccess: () => { setToast('Modules updated'); invalidate(); } });
  const saveSub = useMutation({ mutationFn: () => platformApi.setSubscription(manage!.id, planSel, 'yearly'), onSuccess: () => { setToast('Subscription updated'); invalidate(); } });

  const columns: Column<Tenant>[] = [
    { key: 'code', label: 'Code', width: 150 },
    { key: 'name', label: 'Tenant', render: (r) => <Stack><span>{r.name}</span><Typography variant="caption" color="text.secondary">{r.subdomain ? `${r.subdomain}.platform` : '—'}</Typography></Stack> },
    { key: 'subscriptionPlan', label: 'Plan', render: (r) => <Chip size="small" label={r.subscriptionPlan ?? '—'} variant="outlined" /> },
    { key: 'userLimit', label: 'Users', align: 'right' },
    { key: 'activeModules', label: 'Modules', render: (r) => `${r.activeModules.length}` },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const openManage = (t: Tenant) => { setManage(t); setModules(t.activeModules); setPlanSel(t.subscriptionPlan ?? 'starter'); };
  const toggleModule = (m: string) => setModules((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  return (
    <>
      <PageHeader title="Tenant Management" subtitle="Provision and administer white-label customer tenants." action={<Button startIcon={<Add />} variant="contained" onClick={() => { setForm({ name: '', subdomain: '', adminEmail: '', adminPassword: '', planCode: 'starter', primaryColor: '#1e40af' }); setError(null); setOpen(true); }}>Provision Tenant</Button>} />

      <TableToolbar search={search} onSearch={(v) => { setSearch(v); setPage(1); }} searchPlaceholder="Search name, code, subdomain…" />
      <DataTable columns={columns} rows={list.data?.data ?? []} loading={list.isLoading} page={page} limit={limit} total={list.data?.meta.total ?? 0} onPageChange={setPage} onLimitChange={setLimit} rowKey={(r) => r.id} emptyTitle="No tenants"
        actions={(r) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {!r.isPlatform && <Tooltip title="Manage"><IconButton size="small" onClick={() => openManage(r)}><Settings fontSize="small" /></IconButton></Tooltip>}
            {!r.isPlatform && r.status !== 'suspended' && <Tooltip title="Suspend"><IconButton size="small" color="error" onClick={() => setStatus.mutate({ id: r.id, status: 'suspended' })}><Block fontSize="small" /></IconButton></Tooltip>}
            {!r.isPlatform && r.status === 'suspended' && <Tooltip title="Activate"><IconButton size="small" color="success" onClick={() => setStatus.mutate({ id: r.id, status: 'active' })}><CheckCircle fontSize="small" /></IconButton></Tooltip>}
          </Stack>
        )}
      />

      {/* Provision dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Provision Tenant</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField label="Tenant Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
              <TextField label="Subdomain" value={form.subdomain} onChange={(e) => setForm((f) => ({ ...f, subdomain: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Admin Email" value={form.adminEmail} onChange={(e) => setForm((f) => ({ ...f, adminEmail: e.target.value }))} fullWidth />
              <TextField label="Admin Password" type="password" value={form.adminPassword} onChange={(e) => setForm((f) => ({ ...f, adminPassword: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField select label="Plan" value={form.planCode} onChange={(e) => setForm((f) => ({ ...f, planCode: e.target.value }))} fullWidth>{PLANS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}</TextField>
              <TextField label="Primary Color" type="color" value={form.primaryColor} onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value }))} sx={{ width: 120 }} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.name || !form.adminEmail || form.adminPassword.length < 8 || provision.isPending} onClick={() => provision.mutate()}>Provision</Button></DialogActions>
      </Dialog>

      {/* Manage dialog: subscription + feature flags + usage */}
      <Dialog open={Boolean(manage)} onClose={() => setManage(null)} fullWidth maxWidth="sm">
        <DialogTitle>{manage?.name} — {manage?.code}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="subtitle2">Subscription</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField select size="small" label="Plan" value={planSel} onChange={(e) => setPlanSel(e.target.value)} sx={{ width: 200 }}>{PLANS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}</TextField>
              <Button size="small" variant="outlined" onClick={() => saveSub.mutate()}>Update plan</Button>
            </Stack>
            <Divider />
            <Typography variant="subtitle2">Feature Flags (active modules)</Typography>
            <FormGroup row>
              {MODULES.map((m) => <FormControlLabel key={m} control={<Checkbox size="small" checked={modules.includes(m)} onChange={() => toggleModule(m)} />} label={m.replace(/_/g, ' ')} />)}
            </FormGroup>
            <Button size="small" variant="contained" sx={{ alignSelf: 'flex-start' }} onClick={() => saveModules.mutate()}>Save modules</Button>
            <Divider />
            <Typography variant="subtitle2">Usage</Typography>
            <Typography variant="body2" color="text.secondary">Users: {usage.data?.userCount ?? 0} / {usage.data?.userLimit ?? '—'} · Storage limit: {usage.data?.storageLimitMb ?? '—'} MB</Typography>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setManage(null)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
